import { useState, useEffect, useRef } from "react";
import { BASE_URL } from "../../config";
import { showSuccess, showError, showConfirm } from "../../utils/dialog";
import useAuth from "../../hooks/useAuth";
import { FaTimes } from "react-icons/fa";
import { MdLibraryAdd } from "react-icons/md";
import DateInput from "../../components/DateInput";
import {
  STAFF_DOCUMENT_ACCEPT,
  validateStaffDocumentFile,
} from "../../utils/staffDocumentValidation";
import { certDisplayName } from "../../utils/registrationProvince";

// province: profile province, so Smart Serve shows under that province's program name
const AddCertificateForm = ({ onClose, initialData = null, mode = "add", province = "" }) => {

  const [certificateData, setCertificateData] = useState([]);
  const auth = useAuth();

  const [selectedCertificate, setSelectedCertificate] = useState("");
  const [selectedExpiryDate, setSelectedExpiryDate] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [certificateQuery, setCertificateQuery] = useState("");
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);

  const containerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectCertificate = (certificate) => {
    setSelectedCertificate(certificate);
    setCertificateQuery(certificate);
    setSelectedExpiryDate("");
    setFile(null);
    setShowDropdown(false);
  };

  useEffect(() => {
    if (!initialData) return;
    const initialCertificate = initialData.name || initialData.certificate || "";
    setSelectedCertificate(initialCertificate);
    setCertificateQuery(initialCertificate);
    setSelectedExpiryDate(initialData.expDate || initialData.expiry_date || "");
    setFile(null);
  }, [initialData]);

  const normalizeRequiredFlag = (value) =>
    value === true ||
    value === 1 ||
    String(value || "").toLowerCase() === "true" ||
    String(value || "") === "1";

  const selectedCertificateDef = certificateData.find(
    (certificate) => certificate.name === selectedCertificate,
  );
  const filteredCertificates = certificateData.filter((certificate) =>
    String(certificate.name || "")
      .toLowerCase()
      .includes(certificateQuery.toLowerCase()),
  );
  const requiresExpiryDate = normalizeRequiredFlag(
    selectedCertificateDef?.requires_expiry_date ??
      selectedCertificateDef?.expirydate,
  );
  const requiresUploadedFile = normalizeRequiredFlag(
    selectedCertificateDef?.requires_uploaded_file ?? selectedCertificateDef?.file,
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/personnel/certifications`, { credentials: 'include' });
        const j = await res.json().catch(() => ({}));
        const rows = Array.isArray(j.data) ? j.data : (Array.isArray(j) ? j : []);
        if (!cancelled) {
          setCertificateData(
            rows.map((r) => ({
              id: r.certification_id ?? r.id,
              name: r.name ?? r.certification,
              requires_expiry_date:
                r.requires_expiry_date ?? r.expirydate ?? false,
              requires_uploaded_file:
                r.requires_uploaded_file ?? r.file ?? false,
            })),
          );
        }
      } catch (e) {
        console.error('Failed to load certifications', e);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const accountId = (typeof window !== 'undefined') ? (localStorage.getItem('accountId') || auth?.accountId) : auth?.accountId;
      if (!accountId) return showError('Account not found');
      if (!selectedCertificate) return showError('Select a certificate');
      if (requiresExpiryDate && !selectedExpiryDate) return showError('Expiry date is required');
      if (requiresUploadedFile && !file) return showError('Supporting file is required');
      const expiry = selectedExpiryDate;

      // find cert id
      const found = certificateData.find(c => c.name === selectedCertificate || c.certificate === selectedCertificate);
      const certId = found ? Number(found.id) : null;

      // Confirm user intent
      if (!(await showConfirm('Send request to office/admin for review?'))) return;

      const currentName = initialData?.name || initialData?.certificate || "";
      const currentExpiry = initialData?.expDate || initialData?.expiry_date || "";
      const actionLabel = mode === "edit" ? "Certification to update" : "Certification to add";
      const changeParts = [];
      if (mode === "edit") {
        if (currentName && currentName !== selectedCertificate) {
          changeParts.push(`name: ${currentName} -> ${selectedCertificate}`);
        } else {
          changeParts.push(`name: ${selectedCertificate}`);
        }
        if (expiry || currentExpiry) {
          changeParts.push(`expiry: ${currentExpiry || "none"} -> ${expiry || "none"}`);
        }
      } else {
        changeParts.push(`Add: ${selectedCertificate}${expiry ? ` (expiry: ${expiry})` : ""}`);
      }
      const changes = {
        [actionLabel]: changeParts.join(", "),
      };
      const token = localStorage.getItem('token');

      if (file) {
        changes["Supporting file"] = `Attached: ${file.name}`;
      } else if (mode === "edit" && initialData?.file) {
        changes["Supporting file"] = `Current: ${initialData.file}`;
      }

      const body = new FormData();
      body.append('accountId', String(Number(accountId)));
      body.append('changes', JSON.stringify(changes));
      body.append('attachments', JSON.stringify([]));
      if (file) {
        body.append('files', file);
      }

      const res = await fetch(`${BASE_URL}/api/temp-staff/request-change`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
        body,
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j?.success) {
        console.warn('Request-change failed', j);
        return showError(j?.message || 'Failed to submit change request');
      }

      showSuccess('Your request has been sent to our team for review and update.');
      onClose && onClose();
    } catch (err) {
      console.error('AddCertificate error', err);
      showError('Failed to submit certificate request');
    }
  };

  const title = mode === "edit" ? "Edit Certificate" : "Add Certification";
  const submitLabel = mode === "edit" ? "Request Changes" : "Request Changes";

  return (
  <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
    <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6 relative">
      {/* Close button */}
      <button 
        onClick={onClose} 
        className="absolute top-3 right-3 text-[#DC1F00] hover:bg-[#DC1F0033] p-3 rounded-full"
       >
        ✖
      </button>

      {/* Header */}
      <div className="flex">
        <MdLibraryAdd className="text-buttonRed text-lg w-5 h-5 mt-1 mr-2"/>
        <h2 className="text-xl text-formTitleText font-semibold mb-4">{title}</h2>
      </div>
      
      <div className="h-[1px] bg-[#DC1F004D]"></div>

      {/* Form */}
          <form className="flex-1 px-5 pt-6" onSubmit={handleSubmit}>
        <div className="relative mb-3" ref={containerRef}>
          <label className="block text-sm text-textGray mb-2">Certification</label>
          <input
            type="text"
            value={certificateQuery}
            onChange={(e) => {
              const nextValue = e.target.value;
              setCertificateQuery(nextValue);
              setSelectedCertificate(nextValue);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            placeholder="Select or search certificate"
            className="w-full border rounded-lg px-3 py-2 min-h-[40px] focus:outline-none focus:ring-1 focus:ring-red-400"
          />

          {showDropdown && (
            <div className="absolute left-0 top-full mt-1 w-full bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto z-50">
              {filteredCertificates.length > 0 ? (
                filteredCertificates.map((certificate) => (
                  <div
                    key={certificate.id}
                    className={`px-3 py-2 cursor-pointer hover:bg-red-100 ${
                      selectedCertificate === certificate.name
                        ? "bg-red-50"
                        : ""
                    } ${mode === "edit" && initialData?.name === certificate.name ? "opacity-70" : ""}`}
                    onClick={() => handleSelectCertificate(certificate.name)}
                  >
                    {certDisplayName(certificate.name, province)}
                  </div>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500">
                  No certificates found
                </div>
              )}
            </div>
          )}
        </div>

        {requiresExpiryDate && (
          <>
            <label className="block text-sm text-[#00000080] mb-2">Expiry Date <span className="text-[#00000080]">*</span></label>
            <DateInput
              value={selectedExpiryDate}
              onChange={setSelectedExpiryDate}
              required={requiresExpiryDate}
              className="w-full border border-[#00000033] rounded-lg px-2 py-2 mb-4 focus:ring-1 focus:ring-red-400"
            />
          </>
        )}

        {requiresUploadedFile && (
          <div className="mb-12">
            <label className="block text-sm text-textGray mb-2">Supporting File</label>
            <div
              className={`border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center text-center transition-colors duration-200 ${
                dragging ? "border-[#DC1F00] bg-red-50" : "border-gray-300 hover:border-[#DC1F00]"
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                const droppedFile = e.dataTransfer.files[0];
                if (droppedFile) {
                  const validationMessage = validateStaffDocumentFile(droppedFile);
                  if (validationMessage) {
                    showError(validationMessage);
                    return;
                  }
                  setFile(droppedFile);
                }
              }}
            >
              {file ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-[#DC1F00]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={1.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    <span className="text-sm font-medium text-gray-800">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      className="text-[#DC1F00] hover:bg-red-100 p-1 rounded-full"
                    >
                      <FaTimes />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">File ready to upload</p>
                </div>
              ) : (
                <>
                  {mode === "edit" && initialData?.file ? (
                    <div className="mb-4 text-center">
                      <p className="text-sm text-gray-700">{initialData.file}</p>
                      <p className="text-xs text-gray-500">Upload a file only if you want to replace the current one</p>
                    </div>
                  ) : null}
                  <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-gray-400 mb-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    d="M12 16V8m0 0l-4 4m4-4l4 4m-9 8h10a2 2 0 002-2V7a2 2 0 00-2-2h-3.5a2 2 0 01-1.414-.586l-.707-.707A2 2 0 0010.5 3H7a2 2 0 00-2 2v15a2 2 0 002 2z" 
                  />
                  </svg>

                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium text-[#DC1F00] cursor-pointer hover:underline">
                      Drag & Drop your file
                    </span>{" "}
                    or use the button below
                  </p>

                  <input
                  type="file"
                  accept={STAFF_DOCUMENT_ACCEPT}
                  id="certificateFile"
                  className="hidden"
                  onChange={(e) => {
                    const uploadedFile = e.target.files[0];
                    if (uploadedFile) {
                      const validationMessage = validateStaffDocumentFile(uploadedFile);
                      if (validationMessage) {
                        showError(validationMessage);
                        e.target.value = "";
                        return;
                      }
                      setFile(uploadedFile);
                    }
                  }}
                  />
                  <label
                  htmlFor="certificateFile"
                  className="mt-3 bg-buttonRed hover:bg-buttonRed-hover text-white font-semibold px-4 py-2 rounded-full cursor-pointer"
                  >
                  Upload File
                  </label>
                </>
              )}
            </div> 
          </div>
        )}

        <div className="flex items-center justify-between gap-4 mb-2">
          <button
            onClick={onClose}
            className="w-full border-2 border-buttonWhite hover:bg-buttonWhite-hover text-[#DC1F00] font-semibold px-4 py-2 rounded-full"
          >
          Cancel
          </button>
          <button
            type="submit"
            className="w-full bg-buttonRed hover:bg-buttonRed-hover text-white font-semibold px-4 py-2 rounded-full"
          >
          {submitLabel}
          </button>
        </div>
        <p className="ml-4 py-1 text-xs">Requested change will be sent to our team to review and update.</p>
      </form>
    </div>
  </div>
  )
};

export default AddCertificateForm;
